import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { useInboxCount } from '../hooks/useInbox'
import { useColorMode } from '../hooks/useColorMode'
import { useLanguage } from '../i18n/LanguageProvider'
import { useT } from '../i18n/useT'
import { FlagDE, FlagEN } from '../i18n/flags'
import { APP_VERSION, OPEN_CHANGELOG_EVENT } from '../changelog'
import { BRAND_AMBER } from '../theme'
import { Icon, IconButton } from './ui'

interface HeaderProps {
  onOpenSettings: () => void
  appName: string
}

interface NavItem {
  path: string
  label: string
  badge?: number
}

export default function Header({ onOpenSettings, appName }: HeaderProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout } = useAuth()
  const pendingInbox = useInboxCount()
  const { mode, toggle } = useColorMode()
  const { lang, toggleLang } = useLanguage()
  const { t } = useT()

  const initials =
    user?.fullName
      ?.split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || 'U'

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  const nav: NavItem[] = [
    { path: '/inbox', label: t('Inbox'), badge: pendingInbox },
    { path: '/', label: t('Dashboard') },
    { path: '/invoice-analyser', label: t('Invoice Analyser') },
    { path: '/database', label: t('Database') },
    { path: '/po-tracking', label: t('PO Tracking') },
    ...(user?.role === 'admin' ? [{ path: '/admin', label: t('Admin') }] : []),
  ]

  return (
    <header
      className="fx-header"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 1100,
        background: 'var(--bar)',
        borderBottom: '1px solid var(--line)',
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        padding: '0 20px',
        height: 'var(--header-h)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
        <div
          style={{ fontSize: 17, fontWeight: 700, letterSpacing: '1.4px', color: '#f8fafc' }}
        >
          {appName}
        </div>
        {/* Doubles as the way into the changelog — the version is the one label a
            user already looks at when wondering what changed. */}
        <button
          type="button"
          className="fx-header-version"
          title={t("What's new")}
          onClick={() => window.dispatchEvent(new Event(OPEN_CHANGELOG_EVENT))}
          style={{
            fontFamily: 'inherit',
            fontSize: 11,
            fontWeight: 600,
            color: 'rgba(248,250,252,0.8)',
            background: 'transparent',
            border: '1px solid rgba(248,250,252,0.32)',
            borderRadius: 999,
            padding: '2px 8px',
            whiteSpace: 'nowrap',
            cursor: 'pointer',
          }}
        >
          v{APP_VERSION}
        </button>
      </div>

      {/* flexShrink 0: the nav must never be the element that gives way, or a
          route silently scrolls out of reach behind an invisible overlay
          scrollbar. Everything else in the header yields first (see
          .fx-header-user / .fx-header-version). */}
      <nav
        className="fx-nav"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          marginLeft: 12,
          flexShrink: 0,
        }}
      >
        {nav.map((item) => (
          <button
            key={item.path}
            type="button"
            className="fx-nav-item"
            aria-current={location.pathname === item.path ? 'page' : undefined}
            onClick={() => navigate(item.path)}
          >
            {item.label}
            {item.badge != null && item.badge > 0 && (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: '#0f172a',
                  background: BRAND_AMBER,
                  borderRadius: 999,
                  minWidth: 18,
                  textAlign: 'center',
                  padding: '1px 6px',
                }}
              >
                {item.badge > 99 ? '99+' : item.badge}
              </span>
            )}
          </button>
        ))}
      </nav>

      <div style={{ flex: 1 }} />

      {/* Language before theme, both as the same header pill: the flag carries
          the meaning, the DE/EN label spells it out at the mockup's 12px/700. */}
      <button
        type="button"
        className="fx-header-btn"
        title={lang === 'en' ? 'Zu Deutsch wechseln' : 'Switch to English'}
        aria-label={lang === 'en' ? 'Sprache auf Deutsch umstellen' : 'Switch language to English'}
        onClick={toggleLang}
      >
        {lang === 'de' ? <FlagDE size={18} /> : <FlagEN size={18} />}
        <span className="fx-header-btn-label">{lang === 'de' ? 'DE' : 'EN'}</span>
      </button>

      <button
        type="button"
        className="fx-header-btn"
        title={mode === 'dark' ? t('Switch to light mode') : t('Switch to dark mode')}
        onClick={toggle}
      >
        <Icon name={mode === 'dark' ? 'sun' : 'moon'} size={15} />
        <span className="fx-header-btn-label">
          {mode === 'dark' ? t('Light') : t('Dark')}
        </span>
      </button>

      {user && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginLeft: 6 }}>
          {/* Dropped first when the header gets tight — the avatar still
              identifies the session, and losing a nav item is far worse. */}
          <div className="fx-header-user" style={{ textAlign: 'right', lineHeight: 1.25 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#f8fafc' }}>{user.fullName}</div>
            <div style={{ fontSize: 11, color: 'rgba(248,250,252,0.75)' }}>{user.username}</div>
          </div>
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: '50%',
              background: BRAND_AMBER,
              color: '#111827',
              fontSize: 13,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {initials}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
        <IconButton title={t('Settings')} variant="onTile" onClick={onOpenSettings}>
          <Icon name="settings" size={17} strokeWidth={1.8} />
        </IconButton>
        <IconButton title={t('Sign out')} variant="onTile" onClick={() => void handleLogout()}>
          <Icon name="logout" size={17} />
        </IconButton>
      </div>
    </header>
  )
}
